import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { EmptyFileError, FileReadError, FileReaderHelper, translateFileReadError } from '@/helpers/fileReaderHelper';
import styles from './FundDialogs.module.css';

/** Big enough for a phone photo of an ID card, small enough that a wrong file is caught before it is sent. */
const MAX_DOCUMENT_SIZE_MB = 15;

interface ReactivateFundDialogProps {
  open: boolean;
  onClose: () => void;
  fundId: number;
  fundName: string;
  onSaved: () => void;
}

interface ReactivateFundDtoOut {
  documents: DocumentDtoOut[];
  note: string;
}

const extractServerError = (error: unknown): string | null => {
  const data = (error as AxiosError).response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'message' in data) return String((data as { message: unknown }).message);
  return null;
};

/**
 * HAT-1301: returns a frozen fund to Active. The fund's documents were destroyed when it was frozen,
 * so the coordinator's fresh identity documents are mandatory here - the server refuses without
 * them, and so does this form.
 */
const ReactivateFundDialog = ({ open, onClose, fundId, fundName, onSaved }: ReactivateFundDialogProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: reactivate } = usePostAuthApi<void, ReactivateFundDtoOut>(`funds/${fundId}/management/reactivate`);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setNote('');
      setError(null);
      setIsSaving(false);
    }
  }, [open]);

  const addFiles = (picked: FileList | null) => {
    if (!picked) return;
    setError(null);
    const accepted: File[] = [];
    for (const file of Array.from(picked)) {
      if (file.size === 0) {
        setError(t('emptyFileSelected', { name: file.name }));
        continue;
      }
      if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) {
        setError(t('fundsManagement.reactivateDialog.fileTooLarge', { name: file.name, max: MAX_DOCUMENT_SIZE_MB }));
        continue;
      }
      accepted.push(file);
    }
    setFiles((current) => [...current, ...accepted]);
  };

  const removeFile = (index: number) => setFiles((current) => current.filter((_, i) => i !== index));

  const save = async () => {
    if (files.length === 0) {
      setError(t('fundsManagement.reactivateDialog.documentsRequired'));
      return;
    }
    if (!note.trim()) {
      setError(t('fundsManagement.noteDialog.noteRequired'));
      return;
    }

    setError(null);
    setIsSaving(true);
    try {
      const documents: DocumentDtoOut[] = [];
      for (const file of files) {
        const [document] = await FileReaderHelper.fileToDocumentDtoOut(file, t('fundsManagement.reactivateDialog.documentDescription'));
        documents.push(document);
      }
      await reactivate({ documents, note: note.trim() });
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof EmptyFileError || err instanceof FileReadError) {
        setError(translateFileReadError(err, t));
      } else {
        setError(extractServerError(err) ?? t('fundsManagement.saveError'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen && !isSaving) onClose();
      }}
      title={t('fundsManagement.reactivateDialog.title')}
      maxWidth="600px"
      fitContent
    >
      <div className={styles.dialogForm}>
        <p className={styles.panelSubtitle}>{t('fundsManagement.reactivateDialog.intro', { id: fundId, name: fundName })}</p>

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.reactivateDialog.documentsLabel')}</label>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isSaving}>
              {t('fundsManagement.reactivateDialog.chooseFiles')}
            </Button>
            {files.length === 0 && <span className="body-sm-regular text-(--font-body-3)">{t('fundsManagement.reactivateDialog.noFiles')}</span>}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {files.length > 0 && (
            <ul className="flex flex-col gap-1 m-0 p-0 list-none">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`} className="flex items-center gap-2 body-sm-regular">
                  <span className="truncate">{file.name}</span>
                  <button
                    type="button"
                    className="body-sm-regular text-red-600 underline bg-transparent border-0 cursor-pointer"
                    onClick={() => removeFile(index)}
                    disabled={isSaving}
                  >
                    {t('fundsManagement.reactivateDialog.removeFile')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.formField}>
          <label className={styles.fieldLabel}>{t('fundsManagement.noteDialog.noteLabel')}</label>
          <textarea
            className={styles.dialogTextarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('fundsManagement.noteDialog.notePlaceholder')}
            disabled={isSaving}
          />
        </div>

        {error && <p className={styles.formError}>{error}</p>}

        <div className={styles.formActions}>
          <Button variant="white" onClick={onClose} disabled={isSaving}>
            {t('fundsManagement.cancel')}
          </Button>
          <Button variant="primary" onClick={save} disabled={isSaving}>
            {isSaving ? t('loading') : t('fundsManagement.reactivateDialog.confirm')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default ReactivateFundDialog;
